import React, { useMemo, useRef, useState } from "react";
import { getNode } from "../fs";
import "./notepad.css";

const MENUS = {
  File: ["New", "New window", "Open…", "Save", "Save as…", "Page setup…", "Print…"],
  Edit: ["Undo", "Cut", "Copy", "Paste", "Delete", "Find…", "Replace…", "Select all", "Time/Date"],
  Format: ["Word wrap", "Font…"],
  View: ["Zoom in", "Zoom out", "Restore default zoom", "Status bar"],
  Help: ["View help", "Send feedback", "About Notepad"],
};

export default function NotepadApp({ payload }) {
  const file = payload?.fileId ? getNode(payload.fileId) : null;
  const initial = file?.text ?? payload?.text ?? "";
  const [text, setText] = useState(initial);
  const [wrap, setWrap] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [menu, setMenu] = useState(null);
  const [caret, setCaret] = useState({ line: 1, col: 1 });
  const areaRef = useRef(null);

  const updateCaret = () => {
    const el = areaRef.current;
    if (!el) return;
    const upto = el.value.slice(0, el.selectionStart);
    const lines = upto.split("\n");
    setCaret({ line: lines.length, col: lines[lines.length - 1].length + 1 });
  };

  const pick = (item) => {
    setMenu(null);
    if (item === "Word wrap") setWrap((w) => !w);
    else if (item === "Zoom in") setZoom((z) => Math.min(500, z + 10));
    else if (item === "Zoom out") setZoom((z) => Math.max(10, z - 10));
    else if (item === "Restore default zoom") setZoom(100);
    else if (item === "Select all") areaRef.current?.select();
    else if (item === "Time/Date") {
      const stamp = new Date().toLocaleString();
      setText((t) => `${t}${stamp}`);
    } else if (item === "New") setText("");
  };

  const chars = useMemo(() => text.length, [text]);

  return (
    <div className="np">
      <div className="np-menubar" onMouseLeave={() => setMenu(null)}>
        {Object.keys(MENUS).map((label) => (
          <div className="np-menu" key={label}>
            <button
              className={`np-menu__btn ${menu === label ? "np-menu__btn--on" : ""}`}
              onClick={() => setMenu(menu === label ? null : label)}
              onMouseEnter={() => menu && setMenu(label)}
            >
              {label}
            </button>
            {menu === label && (
              <div className="np-menu__list">
                {MENUS[label].map((item) => (
                  <button
                    key={item}
                    className="np-menu__item"
                    onClick={() => pick(item)}
                  >
                    <span className="np-menu__check">
                      {item === "Word wrap" && wrap ? "✓" : ""}
                    </span>
                    {item}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <textarea
        ref={areaRef}
        className={`np-area ${wrap ? "" : "np-area--nowrap"}`}
        style={{ fontSize: `${(14 * zoom) / 100}px` }}
        value={text}
        spellCheck="false"
        onChange={(e) => {
          setText(e.target.value);
          updateCaret();
        }}
        onKeyUp={updateCaret}
        onClick={updateCaret}
        aria-label={file?.name || "Untitled"}
      />

      <div className="np-status">
        <span>
          Ln {caret.line}, Col {caret.col}
        </span>
        <span>{chars} characters</span>
        <span className="np-status__right">{zoom}%</span>
        <span>Windows (CRLF)</span>
        <span>UTF-8</span>
      </div>
    </div>
  );
}
